import React, { useState, useEffect } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../utils/DateUtils';
import { getKnockoutWinner, getKnockoutAnnotationLines, getWalkoverLabel } from '../utils/CupResult';
import SportsScoreIcon from '@mui/icons-material/SportsSoccer';
import { SvgIcon } from '@mui/material';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YellowCardIcon = () => (
    <SvgIcon style={{ fontSize: '1.1em' }}>
        <rect x="6" y="2" width="12" height="20" rx="2" fill="#fdd835" />
    </SvgIcon>
);

const SecondYellowCardIcon = () => (
    <SvgIcon style={{ fontSize: '1.1em' }}>
        <defs>
            <linearGradient id="cardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="50%" stopColor="#fdd835" />
                <stop offset="50%" stopColor="#e53935" />
            </linearGradient>
        </defs>
        <rect x="6" y="2" width="12" height="20" rx="2" fill="url(#cardGradient)" />
    </SvgIcon>
);

const RedCardIcon = () => (
    <SvgIcon style={{ fontSize: '1.1em' }}>
        <rect x="6" y="2" width="12" height="20" rx="2" fill="#e53935" />
    </SvgIcon>
);

const CardDisplay = ({ cards }) => {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
            {cards && cards.map((card, i) => {
                if (card === 'yellow') {
                    return <YellowCardIcon key={i} />;
                } else if (card === 'second_yellow') {
                    return <SecondYellowCardIcon key={i} />;
                } else if (card === 'red') {
                    return <RedCardIcon key={i} />;
                }
                return null;
            })}
        </span>
    );
};

const getMatchHeader = (match) => {
    if (match.type === 'league') return `Liga, kolejka ${match.gameweek}`;
    if (match.type === 'cup_group') return `Puchar Ligi, kolejka ${match.cup_gameweek}, grupa ${match.group}`;
    if (match.type === 'cup_knockout') return match.stage ? `Puchar Ligi, ${match.stage}` : 'Puchar Ligi';
    return `Kolejka ${match.gameweek}`;
};

const ResultsView = ({ season }) => {
    const navigate = useNavigate();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${BACKEND_URL}/matches/results?season=${season}`);
                setResults(response.data);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [season]);

    const handleRowClick = (matchId) => {
        navigate(`/mecz/${matchId}`);
    };

    // Group consecutive matches with the same header
    const grouped = results.reduce((acc, match) => {
        const header = getMatchHeader(match);
        const lastGroup = acc[acc.length - 1];
        if (lastGroup && lastGroup.header === header) {
            lastGroup.matches.push(match);
        } else {
            acc.push({ header, matches: [match] });
        }
        return acc;
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Ładowanie...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div className="cards-container">
            {grouped.map((group, index) => (
                <Card key={index} className="mb-3">
                    <Card.Header>{group.header}</Card.Header>
                    <Card.Body>
                    {group.matches.map((match, matchIndex) => {
                        const isKnockout = match.type === 'cup_knockout';
                        const winner = isKnockout ? getKnockoutWinner(match) : null;
                        const annotationLines = isKnockout ? getKnockoutAnnotationLines(match) : [];
                        const walkoverLabel = getWalkoverLabel(match);
                        const isDoubleWalkover = match.walkover === 'both';
                        const teamNameStyle = (side) => ({
                            fontSize: '0.9em',
                            textAlign: 'center',
                            ...(winner && winner !== side ? { color: '#999', fontWeight: 400 } : {})
                        });
                        return (
                        <Card key={matchIndex} className="mb-3" style={{ cursor: 'pointer' }} onClick={() => handleRowClick(match.id)}>
                            <Card.Header style={{ padding: '15px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
                                    {/* Gospodarze */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {match.home_team_logo && (
                                            <img
                                                src={`/logos/${match.home_team_logo}`}
                                                alt={match.home_team}
                                                style={{ height: '40px', marginBottom: '5px', objectFit: 'contain' }}
                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                            />
                                        )}
                                        <strong style={teamNameStyle('home')}>{match.home_team}</strong>
                                    </div>

                                    {/* Wynik i data */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.75em', color: '#666', marginBottom: '3px' }}>{formatDateTime(match.date)}</span>
                                        <span style={{ fontSize: '2.2em', fontWeight: 'bold' }}>
                                            {isDoubleWalkover
                                                ? <span style={{ fontSize: '0.6em' }}>0:3 / 0:3</span>
                                                : `${match.home_goals} - ${match.away_goals}`}
                                        </span>
                                        {walkoverLabel && (
                                            <div style={{ fontSize: '0.7em', color: '#666', marginTop: '2px', textAlign: 'center' }}>
                                                {walkoverLabel}
                                            </div>
                                        )}
                                        {annotationLines.length > 0 && (
                                            <div style={{ fontSize: '0.7em', color: '#666', marginTop: '2px', textAlign: 'center', lineHeight: 1.3 }}>
                                                {annotationLines.map((line, i) => <div key={i}>{line}</div>)}
                                            </div>
                                        )}
                                    </div>

                                    {/* Goście */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {match.away_team_logo && (
                                            <img
                                                src={`/logos/${match.away_team_logo}`}
                                                alt={match.away_team}
                                                style={{ height: '40px', marginBottom: '5px', objectFit: 'contain' }}
                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                            />
                                        )}
                                        <strong style={teamNameStyle('away')}>{match.away_team}</strong>
                                    </div>
                                </div>
                            </Card.Header>

                            <Card.Body>
                                <div style={{ display: 'flex', gap: '30px' }}>
                                    {/* Strzelcy gospodarzy */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ marginTop: '0px' }}>
                                            {match.home_goalscorers && match.home_goalscorers.length > 0 ? (
                                                match.home_goalscorers.map((scorer, scorerIndex) => (
                                                    <div key={scorerIndex} style={{ marginBottom: '6px', fontSize: '0.85em' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            {scorer.player_id ? (
                                                                <>
                                                                    {scorer.first_name} {scorer.last_name}
                                                                    {Array.from({ length: scorer.goal_count }).map((_, i) => (
                                                                        <SportsScoreIcon key={i} style={{ fontSize: '1.1em' }} />
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span style={{ color: '#d32f2f' }}>Bramka samobójcza</span>
                                                                    {Array.from({ length: scorer.goal_count }).map((_, i) => (
                                                                        <SportsScoreIcon key={i} style={{ fontSize: '1.1em', color: '#d32f2f' }} />
                                                                    ))}
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))
                                            ) : null}
                                        </div>
                                        
                                        {/* Kartki gospodarzy */}
                                        {match.home_cards && match.home_cards.length > 0 && (
                                            <div style={{ marginTop: '12px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                                                {match.home_cards.map((player, cardIndex) => (
                                                    <div key={cardIndex} style={{ marginBottom: '4px', fontSize: '0.85em' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            {player.first_name} {player.last_name}
                                                            <CardDisplay cards={player.cards} />
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Strzelcy gości */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ marginTop: '0px' }}>
                                            {match.away_goalscorers && match.away_goalscorers.length > 0 ? (
                                                match.away_goalscorers.map((scorer, scorerIndex) => (
                                                    <div key={scorerIndex} style={{ marginBottom: '6px', fontSize: '0.85em' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            {scorer.player_id ? (
                                                                <>
                                                                    {scorer.first_name} {scorer.last_name}
                                                                    {Array.from({ length: scorer.goal_count }).map((_, i) => (
                                                                        <SportsScoreIcon key={i} style={{ fontSize: '1.1em' }} />
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span style={{ color: '#d32f2f' }}>Bramka samobójcza</span>
                                                                    {Array.from({ length: scorer.goal_count }).map((_, i) => (
                                                                        <SportsScoreIcon key={i} style={{ fontSize: '1.1em', color: '#d32f2f' }} />
                                                                    ))}
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))
                                            ) : null}
                                        </div>
                                        
                                        {/* Kartki gości */}
                                        {match.away_cards && match.away_cards.length > 0 && (
                                            <div style={{ marginTop: '12px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                                                {match.away_cards.map((player, cardIndex) => (
                                                    <div key={cardIndex} style={{ marginBottom: '4px', fontSize: '0.85em' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            {player.first_name} {player.last_name}
                                                            <CardDisplay cards={player.cards} />
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                        );
                    })}
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
};

export default ResultsView;